import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../utils/translationHelpers';
import { logger } from '../utils/logger';
import './InstallSheets.css';
import './InstallPrompt.css';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export interface InstallPromptProps {
  show?: boolean;
  onDismiss?: () => void;
}

export const InstallPrompt: React.FC<InstallPromptProps> = ({
  show: showFromParent,
  onDismiss,
}) => {
  const { t_nested, currentLanguage } = useLanguage();
  const isRtl = currentLanguage === 'ar';
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return;
    }

    const dismissed = localStorage.getItem('installPromptDismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed, 10);
      const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        return;
      }
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  useEffect(() => {
    if (!showFromParent) {
      setShowPrompt(false);
      return;
    }
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return;
    }
    const dismissed = localStorage.getItem('installPromptDismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed, 10);
      const daysSinceDismissed =
        (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        return;
      }
    }
    if (!deferredPrompt) {
      return;
    }
    setShowPrompt(true);
  }, [showFromParent, deferredPrompt]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      logger.info('User accepted the install prompt');
    }

    setDeferredPrompt(null);
    setShowPrompt(false);
    onDismiss?.();
  };

  const handleDismiss = () => {
    localStorage.setItem('installPromptDismissed', Date.now().toString());
    setShowPrompt(false);
    onDismiss?.();
  };

  if (!showPrompt || !deferredPrompt) {
    return null;
  }

  return (
    <div className="chromium-install-overlay" onClick={handleDismiss}>
      <div
        className="chromium-install-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="chromium-install-title"
        dir={isRtl ? 'rtl' : 'ltr'}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="chromium-install-body">
          <div className="install-sheet-header">
            <h2 className="install-sheet-title" id="chromium-install-title">
              {t_nested('install.title')}
            </h2>
            <button
              type="button"
              className="install-sheet-close"
              onClick={handleDismiss}
              aria-label={getTranslation(
                t_nested,
                'install.close',
                'Close'
              )}
            >
              ×
            </button>
          </div>
          <div className="install-sheet-content-inner">
            <img
              src="/icons/icon-192x192.png"
              alt=""
              className="install-sheet-icon"
            />
            <p className="install-sheet-message">
              {t_nested('install.mobileMessage')}
            </p>
          </div>
          <div className="install-sheet-actions">
            <button
              type="button"
              className="install-sheet-btn install-sheet-btn-primary"
              onClick={handleInstall}
            >
              {getTranslation(t_nested, 'install.installNow', 'Install Now')}
            </button>
            <button
              type="button"
              className="install-sheet-btn install-sheet-btn-secondary"
              onClick={handleDismiss}
            >
              {getTranslation(t_nested, 'install.maybeLater', 'Maybe Later')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
